import { observer } from 'mobx-react'
import React, { Key, useState } from 'react'

import { useAppTheme } from '@flowwai-frontend/bps-officer-web/style-guide'
import {
   Badge,
   CustomOptionType,
   Item,
   Picker,
   PickerContextProvider
} from '@flowwai-frontend/design-system'
import { useCustomT } from '@flowwai-frontend/shared/common-components'
import {
   ChevronDownIcon,
   FilterLinesIcon,
   TickMark
} from '@flowwai-frontend/design-system/icons'
import { TemplateBaseDetailsType } from '@flowwai-frontend/bps-officer-web/common/data-access'

import {
   getItemTextWithIconContainerThemeStyles,
   getTemplateCustomOptionThemeStyles,
   tickIconClassName
} from './styles'
import './styles.css'

interface Props {
   selectedTemplateId: string
   setSelectedTemplateId: (id: string) => void
   templates: TemplateBaseDetailsType[]
}

const GSTemplatePicker = (props: Props) => {
   const { selectedTemplateId, setSelectedTemplateId, templates } = props

   const [isOpenPopOver, setIsOpenPopOver] = useState(false)

   const t = useCustomT('globalSearch:searchModal')
   const { theme } = useAppTheme()
   const { salesTemplatePickerTheme } = theme.globalSearch.searchModal

   const onChangeTemplate = (id: Key) => {
      setSelectedTemplateId(id as string)
   }

   const renderRightIcon = (color: string): React.ReactElement =>
      isOpenPopOver ? (
         <div
            style={{
               transform: 'rotate(180deg)',
               transition: 'transform 0.5s',
               flexShrink: 0
            }}
         >
            <ChevronDownIcon height={12} width={12} fill={color} />
         </div>
      ) : (
         <div style={{ transition: 'transform 0.5s', flexShrink: 0 }}>
            <ChevronDownIcon height={12} width={12} fill={color} />
         </div>
      )

   const renderCustomOption = (
      props: CustomOptionType<any>
   ): React.ReactElement => {
      const { item, isSelected, optionRef, optionProps } = props

      return (
         <div
            {...optionProps}
            ref={optionRef}
            className={getItemTextWithIconContainerThemeStyles(
               salesTemplatePickerTheme,
               isSelected
            )}
         >
            <span
               className={getTemplateCustomOptionThemeStyles(
                  salesTemplatePickerTheme.salesTemplateColor
               )}
               title={item.textValue}
            >
               {item.textValue}
            </span>
            {isSelected ? (
               <div className={tickIconClassName}>
                  <TickMark
                     fill={salesTemplatePickerTheme.tickIconColor}
                     width={20}
                     height={20}
                  />
               </div>
            ) : null}
         </div>
      )
   }

   const renderValueContainer = () => {
      const templateName = templates.find(
         template => template.templateId === selectedTemplateId
      )?.templateName

      return (
         <Badge
            size='Small'
            variant={'PRIMARY'}
            rightElement={renderRightIcon}
            leftElement={color => (
               <FilterLinesIcon width={12} height={12} stroke={color} />
            )}
            isOutline={true}
            containerClassName='flex-block'
            badgeTextClassName='truncate value-container-badge-styles'
         >
            {t('templateSelector', {
               templateName: templateName
            })}
         </Badge>
      )
   }

   const popOverStyles = {
      width: '242px',
      marginTop: '5px',
      boxShadow:
         ' 0px 4px 6px -2px rgba(16, 24, 40, 0.03), 0px 12px 16px -4px rgba(16, 24, 40, 0.08)'
   }

   const onOpenChange = (value: boolean) => {
      setIsOpenPopOver(value)
   }

   return (
      <PickerContextProvider renderValueContainer={renderValueContainer}>
         <Picker
            selectedKey={selectedTemplateId}
            onSelectionChange={onChangeTemplate}
            customOption={(args: CustomOptionType<any>) =>
               renderCustomOption(args)
            }
            size={'Small'}
            focusRingVariant='None'
            buttonClassName={`template-picker`}
            pickerButtonClassName='w-[100%]'
            popOverStyles={popOverStyles}
            showPickerButtonRightIcon={false}
            onOpenChange={onOpenChange}
            showLabel={false}
         >
            {templates.map(option => (
               <Item key={option.templateId}>{option.templateName}</Item>
            ))}
         </Picker>
      </PickerContextProvider>
   )
}

export default observer(GSTemplatePicker)
